"use client";

import Image from "next/image";
import Link from "next/link";
import { Star } from "lucide-react";
import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";

if (typeof window !== "undefined") {
    gsap.registerPlugin(ScrollTrigger);
}

const PROCESS_STEPS = [
    {
        id: "01",
        title: "Assessment and Planning",
        description: "In this initial step, the focus is on understanding the specific needs of your business, then planning the network, phones, and systems that fit.",
        image: "/images/hero/slide-1.webp",
    },
    {
        id: "02",
        title: "Solution and development",
        description: "With a plan in place, the development and implementation process begins.",
        image: "/images/hero/slide-2.webp",
    },
    {
        id: "03",
        title: "Support and Optimization",
        description: "After the solution is deployed, ongoing support and optimisation are essential for long-term success.",
        image: "/images/hero/slide-3.webp",
    }
];

export function WorkingProcess() {
    const containerRef = useRef<HTMLElement>(null);

    useGSAP(() => {
        // Animate Left Column (Content)
        gsap.from(".process-content", {
            x: -50,
            opacity: 0,
            duration: 1,
            scrollTrigger: {
                trigger: ".process-content",
                start: "top 80%",
            }
        });

        // Animate Right Column (Steps) Staggered
        gsap.from(".process-step", {
            x: 50,
            opacity: 0,
            duration: 0.8,
            stagger: 0.3, // Sequential delay
            ease: "power2.out",
            scrollTrigger: {
                trigger: ".process-steps-container",
                start: "top 75%",
            }
        });

    }, { scope: containerRef });

    return (
        <section ref={containerRef} className="bg-[#eff3fa] relative overflow-hidden">
            <div className="container mx-auto px-4 py-24">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

                    {/* Left Column: Sticky Content */}
                    <div className="lg:sticky lg:top-32 process-content">
                        <div className="flex items-center gap-2 mb-4">
                            <Star className="w-4 h-4 text-blue-900 fill-blue-900" />
                            <p className="text-[#566e7c] text-sm font-bold uppercase tracking-widest font-sans">
                                WORKING PROCESS
                            </p>
                        </div>

                        <h2 className="mb-6 font-sans text-3xl font-semibold leading-tight text-[#001746] lg:text-4xl xl:text-[54px]">
                            Explore our best<br />
                            proven work <span className="underline decoration-1 underline-offset-8 decoration-gray-300">procedure</span>
                        </h2>

                        <p className="text-[#566e7c] text-base md:text-lg leading-relaxed mb-8 md:mb-10 max-w-xl">
                            At CNS, we follow a structured approach to deliver exceptional IT solutions. Our process ensures that every project is executed efficiently, meeting your specific business needs and goals.
                        </p>

                        <Link
                            href="/contact"
                            className="inline-block px-8 py-4 rounded-full border border-[#001746] text-[#001746] font-semibold hover:bg-[#001746] hover:text-white transition-colors duration-300"
                        >
                            Explore more
                        </Link>
                    </div>

                    {/* Right Column: Timeline List */}
                    <div className="flex flex-col gap-12 relative mt-8 md:mt-0 process-steps-container">
                        {PROCESS_STEPS.map((step, index) => (
                            <div
                                key={step.id}
                                className={`process-step group flex flex-row gap-5 md:gap-8 items-start relative ${index === 1 ? '' : 'md:ml-24'
                                    }`}
                            >
                                {/* Number Badge & Image */}
                                <div className="relative flex-shrink-0">
                                    <div className="absolute -top-3 -left-3 w-8 h-8 md:w-10 md:h-10 bg-white rounded-full shadow-lg flex items-center justify-center z-10 font-bold text-sm md:text-base text-[#001746]">
                                        {step.id}
                                    </div>
                                    <div className="w-20 h-20 md:w-32 md:h-32 min-w-[5rem] md:min-w-[8rem] rounded-2xl overflow-hidden relative shadow-md bg-gray-200 transition-transform duration-300 group-hover:scale-105">
                                        <Image
                                            src={step.image}
                                            alt={step.title}
                                            fill
                                            className="object-cover"
                                            sizes="128px"
                                        />
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="pt-1 md:pt-2 text-left flex-1">
                                    <h3 className="text-xl md:text-2xl font-bold text-[#001746] mb-2 group-hover:text-blue-600 transition-colors leading-tight">
                                        {step.title}
                                    </h3>
                                    <p className="text-[#566e7c] text-sm md:text-base leading-relaxed">
                                        {step.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </div>

            {/* Bottom Blue Marquee Bar */}
            <div className="bg-[#0056D2] py-4 overflow-hidden relative whitespace-nowrap">
                <div className="inline-flex animate-marquee-infinite">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="flex items-center mx-4">
                            <span className="text-white font-bold text-sm md:text-base tracking-[0.2em] mx-8">PROFESSIONAL IT SOLUTIONS</span>
                            <span className="text-blue-300 mx-4">•</span>
                            <span className="text-white font-bold text-sm md:text-base tracking-[0.2em] mx-8">CONTACT OUR EXPERT TEAM</span>
                            <span className="text-blue-300 mx-4">•</span>
                            <span className="text-white font-bold text-sm md:text-base tracking-[0.2em] mx-8">CONNECT WITH CONFIDENCE</span>
                            <span className="text-blue-300 mx-4">•</span>
                        </div>
                    ))}
                </div>
                <style jsx>{`
                    .animate-marquee-infinite {
                        display: inline-flex;
                        animation: marquee 20s linear infinite;
                    }
                    @keyframes marquee {
                        0% { transform: translateX(0); }
                        100% { transform: translateX(-50%); }
                    }
                `}</style>
            </div>
        </section>
    );
}
